/**
 * Modal de Conferência de Saída Web — Fase 2.
 *
 * Por linha: lote esperado (exibição), validade esperada (exibição),
 *            campo "Lote conferido" (obrigatório, deve bater com lote separado),
 *            campo "Validade conferida" (obrigatório, deve bater com validade do lote),
 *            campo "Quantidade conferida" (obrigatório),
 *            campo "Motivo" (obrigatório quando qty_conf < qty_req),
 *            campo "Observação" (obrigatório quando motivo = OUTROS).
 *
 * Parâmetros do tenant (carregados de GET /companies/<id>):
 *   out_allow_partial_conference — quando false, bloqueia conferência parcial
 *   out_require_divergence_auth  — quando true, avisa que permissão de autorização é exigida
 *
 * Aceita saídas em status 30 (Separada), 40 (Conferindo) ou 50 (Unpick).
 * Bloqueia status 60 ou superior.
 * Não altera o contrato PUT/PATCH legado do Coletor PWA.
 */

import api from '../api.js';
import { notify } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import { openShortageModal, hasShortage } from './outputs-shortage.js';
import { openUnpickRequestModal, unpickStatusBadge, UNPICK_STATUS_LABELS } from './outputs-unpick.js';
import {
	openPickingBoxesForOutputModal,
	openCloseBoxModal,
	BOX_CTRL_HINT,
	escHtml as boxEscHtml,
} from '../utils/output-picking-box-ops.js';

/** Mapa de status para rótulo legível */
const STATUS_LABEL = {
	10: 'Criada',
	20: 'Picking',
	30: 'Separada',
	40: 'Conferindo',
	50: 'Unpick',
	60: 'Conferida',
	65: 'Faturando',
	70: 'Faturada',
	80: 'Expedição',
	90: 'Encerrada',
	100: 'Cancelada',
	999: 'Excluída',
};

const VOLUME_TYPES = [
	{ value: 'C', label: 'Caixa' },
];

/** Motivos de divergência — espelha DIVERGENCE_REASON_CODES do backend */
const DIVERGENCE_REASONS = [
	{ value: 'FALTA_FISICA',          label: 'Falta física' },
	{ value: 'AVARIA',                label: 'Avaria' },
	{ value: 'ERRO_PICKING',          label: 'Erro de picking' },
	{ value: 'QUANTIDADE_DIVERGENTE', label: 'Quantidade divergente' },
	{ value: 'CORRECAO_OPERACIONAL',  label: 'Correção operacional' },
	{ value: 'OUTROS',                label: 'Outros' },
];

/**
 * Normaliza string de lote para comparação:
 * remove espaços nas pontas, converte para minúsculas.
 * @param {string|null|undefined} s
 * @returns {string}
 */
function normalizeLot(s) {
	return s != null ? String(s).trim().toLowerCase() : '';
}

function escapeHtml(value) {
	if (value == null) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/**
 * Converte data digitada pelo usuário para YYYY-MM-DD.
 * Formato principal: DD/MM/AAAA (produzido pela máscara).
 * Aceita também YYYY-MM-DD para compatibilidade.
 * Retorna null se não reconhecer o formato.
 * @param {string} raw
 * @returns {string|null}
 */
function parseInputDate(raw) {
	if (!raw) return null;
	const s = String(raw).trim();

	// YYYY-MM-DD
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
		const [y, m, d] = s.split('-');
		const dt = new Date(`${y}-${m}-${d}T00:00:00`);
		return isNaN(dt.getTime()) ? null : `${y}-${m}-${d}`;
	}

	// DD/MM/AAAA
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
		const [d, m, y] = s.split('/');
		const dt = new Date(`${y}-${m}-${d}T00:00:00`);
		return isNaN(dt.getTime()) ? null : `${y}-${m}-${d}`;
	}

	return null;
}

/**
 * Formata data ISO (YYYY-MM-DD) como DD/MM/AAAA, ou retorna '—'.
 * @param {string|null} iso
 * @returns {string}
 */
function fmtDate(iso) {
	if (!iso) return '—';
	const s = String(iso);
	// suporta 'YYYY-MM-DD' e 'YYYY-MM-DDTHH:...'
	const [y, m, d] = s.split('T')[0].split('-');
	return `${d}/${m}/${y}`;
}

/**
 * Marca input com borda vermelha (erro) ou remove a marcação.
 * @param {HTMLElement} el
 * @param {boolean} hasError
 */
function markError(el, hasError) {
	el.style.outline = hasError ? '2px solid #c00' : '';
	el.style.background = hasError ? '#fff0f0' : '';
}

/**
 * Aplica máscara DD/MM/AAAA no input de validade.
 * O usuário digita apenas números; as barras são inseridas automaticamente.
 * @param {HTMLInputElement} input
 */
function applyDateMask(input) {
	const digits = input.value.replace(/\D/g, '').slice(0, 8);
	let masked = digits.slice(0, 2);
	if (digits.length > 2) masked += '/' + digits.slice(2, 4);
	if (digits.length > 4) masked += '/' + digits.slice(4, 8);
	input.value = masked;
}

/**
 * Mostra ou oculta a sub-linha de motivo/obs (conf-div-row) abaixo da linha principal.
 * @param {HTMLElement} mainRow - <tr data-number="..."> (linha principal)
 * @param {boolean} show
 * @param {boolean} allowPartial - quando false, realça a linha em vermelho
 */
function toggleDivergenceFields(mainRow, show, allowPartial) {
	const tbody  = mainRow.parentElement;
	const num    = mainRow.dataset.number;
	const divRow = tbody?.querySelector(`.conf-div-row[data-number="${num}"]`);
	if (!divRow) return;

	divRow.style.display = show ? '' : 'none';

	if (!allowPartial && show) {
		mainRow.querySelectorAll('td').forEach(td => { td.style.background = '#fff0f0'; });
	} else {
		mainRow.querySelectorAll('td').forEach(td => { td.style.background = ''; });
	}
}

/**
 * Abre o modal de Conferência de Saída Web.
 *
 * @param {string} companyId - UUID da empresa
 * @param {number|string} outputId - ID da saída física
 * @param {function} [onSuccess] - callback chamado após conferência persistida com sucesso
 */
export default async function openConferenceModal(companyId, outputId, onSuccess)
{
	// Carregar dados da saída, parâmetros da empresa, checkpoint do coletor e unpicking em paralelo
	const [output, companyRaw, unpickRespRaw, checkpointRaw] = await Promise.all([
		api.call(api.select_output,  companyId, outputId),
		api.call(api.select_company, companyId),
		fetch(`${api.HOST}/companies/${companyId}/outputs/${outputId}/unpick-requests`, {
			method: 'GET', credentials: 'include',
		}).then(r => r.ok ? r.json() : []).catch(() => []),
		api.call(api.get_collector_conference_checkpoint, companyId, outputId)
			.then(r => r.ok ? r.json() : null).catch(() => null),
	]);
	if (!output) return;

	/** @type {object|null} checkpoint parcial pending do ColetorPWA */
	const collectorCheckpoint = checkpointRaw && checkpointRaw.status === 'pending'
		? checkpointRaw : null;
	const checkpointByNumber = {};
	if (collectorCheckpoint) {
		for (const it of (collectorCheckpoint.items_ok || [])) {
			checkpointByNumber[it.output_item_number] = { ...it, origin: 'ok' };
		}
		for (const it of (collectorCheckpoint.divergent_items || [])) {
			checkpointByNumber[it.output_item_number] = { ...it, origin: 'divergent' };
		}
	}

	// Mapa de item_number → solicitação de unpicking ativa (REQUESTED ou APPROVED)
	const unpickByItem = {};
	for (const req of (unpickRespRaw || [])) {
		if (req.status === 'REQUESTED' || req.status === 'APPROVED') {
			unpickByItem[req.output_item_number] = req;
		}
	}

	// Parâmetros do tenant (default = comportamento legado / permissivo)
	const allowPartial    = companyRaw?.out_allow_partial_conference !== false;   // true por padrão
	const requireAuth     = companyRaw?.out_require_divergence_auth  === true;    // false por padrão
	const enableBoxCtrl   = companyRaw?.out_enable_box_control       === true;    // false por padrão
	const packagingActive = !!(companyRaw?.pkg_use_hierarchy && companyRaw?.pkg_optimize_picking
		&& companyRaw?.pkg_rollback_legacy !== true);

	/** @param {object} item @returns {boolean} */
	function itemRequiresPickingBox(item) {
		if (!enableBoxCtrl) return false;
		if (!packagingActive) return true;
		return !item.is_tertiary_volume;
	}

	/** @param {object} item @param {number} qtySup @returns {number} */
	function tertiaryVolumeUnits(item, qtySup) {
		if (!item?.is_tertiary_volume) return 0;
		const supplied = Number(qtySup) || 0;
		if (supplied <= 0) return 0;
		const factor = Number(item.packaging_factor) || 1;
		if (factor > 1) return Math.floor(supplied / factor);
		return 1;
	}

	/** @returns {number} */
	function countTertiaryVolumes() {
		return (items || []).reduce((sum, item) => {
			const qtySup = parseInt(item.quantity_supplied ?? item.quantity_required ?? 0, 10);
			return sum + tertiaryVolumeUnits(item, qtySup);
		}, 0);
	}

	/** @returns {number} */
	function expectedConferenceVolumes() {
		if (!enableBoxCtrl) return 0;
		return boxClosedCount + (packagingActive ? countTertiaryVolumes() : 0);
	}

	/** @returns {boolean} */
	function needsSealedBoxesForConference() {
		if (!enableBoxCtrl) return false;
		return (items || []).some((item) => {
			if (!itemRequiresPickingBox(item)) return false;
			const qtySup = parseInt(item.quantity_supplied ?? item.quantity_required ?? 0, 10);
			return qtySup > 0;
		});
	}

	// ── Controle de caixas: carregar dados se parâmetro ativo ────────────────
	let pickingBoxes      = null;
	let boxSumByLine      = {};   // output_item_number → qty somada de caixas lacradas
	let boxOpenCount      = 0;
	let boxClosedCount    = 0;
	let boxCanceledCount  = 0;
	let boxClosedTotalItems = 0;
	let boxClosedTotalQty   = 0;

	function recomputeBoxTotals() {
		boxSumByLine = {};
		boxOpenCount = 0;
		boxClosedCount = 0;
		boxCanceledCount = 0;
		boxClosedTotalItems = 0;
		boxClosedTotalQty = 0;
		for (const box of (pickingBoxes || [])) {
			const boxStatus = Number(box.status);
			if (boxStatus === 10) {
				boxOpenCount++;
			} else if (boxStatus === 20) {
				boxClosedCount++;
				const bItems = Array.isArray(box.items) ? box.items : [];
				boxClosedTotalItems += bItems.length;
				for (const bi of bItems) {
					const qty = Number(bi.quantity) || 0;
					boxClosedTotalQty += qty;
					const num = bi.output_item_number;
					boxSumByLine[num] = (boxSumByLine[num] || 0) + qty;
				}
			} else if (boxStatus === 30) {
				boxCanceledCount++;
			}
		}
	}

	if (enableBoxCtrl) {
		try {
			const boxData = await api.call(api.list_output_picking_boxes, companyId, outputId);
			pickingBoxes = boxData?.boxes || [];
		} catch (_) {
			pickingBoxes = null;
		}

		if (!pickingBoxes) {
			notify(
				'Controle de caixas ativo: não foi possível carregar os itens das caixas.',
				'Conferência de Saída'
			);
			return;
		}

		recomputeBoxTotals();
	}

	const ALLOWED = [30, 40, 50];
	if (!ALLOWED.includes(output.status))
	{
		notify(
			output.status >= 60
				? `Saída ${outputId} já foi conferida (status ${output.status} — ${STATUS_LABEL[output.status] || output.status}). Edição não permitida.`
				: `Status ${output.status} (${STATUS_LABEL[output.status] || '?'}) não permite conferência web.`,
			'Conferência de Saída'
		);
		return;
	}

	const items = output.items || [];

	// Opções do select de motivo de divergência
	const reasonOptions = DIVERGENCE_REASONS.map(r =>
		`<option value="${r.value}">${r.label}</option>`
	).join('');

	const itemRows = items.map(item =>
	{
		const sku      = item.product_sku  ? String(item.product_sku).trim()  : '';
		const name     = item.product_name ? String(item.product_name).trim() : `Produto #${item.product_id}`;
		const lotNum   = item.lot_number   ? String(item.lot_number).trim()   : null;
		const validISO = item.expiration_date ? String(item.expiration_date).split('T')[0] : null;
		const validFmt = fmtDate(validISO);

		// Colorir validade quando próxima (≤90 dias) ou vencida
		let validStyle = 'color:#555';
		if (validISO) {
			const daysLeft = (new Date(validISO) - new Date()) / 86400000;
			if (daysLeft < 0)        validStyle = 'color:#c00;font-weight:bold';
			else if (daysLeft <= 90) validStyle = 'color:#c80';
		}

		// Saídas sem lote (item.lot_id null): não exige lote/validade
		const hasLot = item.lot_id != null;

		// Caixas: quantidade pré-preenchida e diff inicial quando controle ativo
		const requiresBox = enableBoxCtrl && itemRequiresPickingBox(item);
		const qtySupSep   = parseInt(item.quantity_supplied ?? item.quantity_required ?? 0, 10);
		const bQty        = enableBoxCtrl
			? (requiresBox ? (boxSumByLine[item.number] || 0) : qtySupSep)
			: 0;
		const bQtyReq   = parseInt(item.quantity_required, 10) || 0;
		const bDiff     = bQty - bQtyReq;
		const cpItem   = checkpointByNumber[item.number];
		const cpOrigin = cpItem?.origin;
		// Com controle de caixas, qty da linha com caixa vem das caixas lacradas (validação no submit).
		const boxQtyWins = enableBoxCtrl && requiresBox;
		const prefQtyVal = boxQtyWins
			? bQty
			: (cpItem != null
				? parseInt(cpItem.quantity_supplied ?? 0, 10)
				: (enableBoxCtrl ? bQty : NaN));
		const prefQty = boxQtyWins
			? String(bQty)
			: (cpItem != null
				? String(cpItem.quantity_supplied ?? '')
				: (enableBoxCtrl ? String(bQty) : ''));
		const prefLot = cpItem?.lot_confirmed ? escapeHtml(String(cpItem.lot_confirmed)) : '';
		const prefExp = cpItem?.expiration_confirmed ? fmtDate(cpItem.expiration_confirmed) : '';

		const diffQty = !isNaN(prefQtyVal) ? prefQtyVal - bQtyReq : bDiff;
		const bDiffStr  = !isNaN(diffQty) ? (diffQty === 0 ? '✓' : String(diffQty)) : (bDiff === 0 ? '✓' : String(bDiff));
		const bDiffColor = !isNaN(diffQty)
			? (diffQty === 0 ? '#2a7' : (diffQty < 0 ? '#c80' : '#c00'))
			: (bDiff === 0 ? '#2a7' : (bDiff < 0 ? '#c80' : '#c00'));

		const originBadge = cpOrigin === 'ok'
			? '<div style="font-size:0.68rem;color:#1a6e38;font-weight:600;margin-top:2px">Conferido no Coletor</div>'
			: cpOrigin === 'divergent'
				? '<div style="font-size:0.68rem;color:#b45309;font-weight:600;margin-top:2px">Divergente no Coletor</div>'
				: (collectorCheckpoint && !cpItem)
					? '<div style="font-size:0.68rem;color:#888;margin-top:2px">Não conferido</div>'
					: '';

		const rowBg = cpOrigin === 'divergent'
			? 'background:#fff8e6!important;'
			: cpOrigin === 'ok'
				? 'background:#f0faf4!important;'
				: '';

		const initDivShow = cpOrigin === 'divergent' && !isNaN(prefQtyVal) && prefQtyVal < bQtyReq;

		// Coluna de unpicking: exibe badge se houver solicitação ativa, ou botão para solicitar
		const activeUnpick = unpickByItem[item.number];
		const unpickCell = activeUnpick
			? `<div class="text-center" style="font-size:0.8rem">${unpickStatusBadge(activeUnpick.status)}</div>`
			: `<button class="btn btn-sm btn-outline-warning conf-unpick-btn" data-number="${escapeHtml(item.number)}"
					style="font-size:0.75rem;padding:2px 6px;white-space:nowrap" title="Solicitar unpicking para esta linha">
					↩ Unpick
				</button>`;

		return `
			<tr data-number="${escapeHtml(item.number)}" data-cp-origin="${cpOrigin || ''}" style="${rowBg}">
				<td style="padding:5px 8px;white-space:nowrap;color:#888;font-size:0.8rem;vertical-align:middle;${rowBg}">${escapeHtml(item.number)}</td>
				<td style="padding:5px 8px;min-width:160px;vertical-align:middle;${rowBg}">
					<div style="font-size:0.72rem;color:#666;line-height:1.2">${escapeHtml(sku)}</div>
					<div style="font-size:0.85rem;line-height:1.3">${escapeHtml(name)}</div>
					${originBadge}
				</td>
				<td style="padding:5px 8px;white-space:nowrap;font-size:0.8rem;vertical-align:middle">
					${hasLot
						? `<div style="font-weight:500">${escapeHtml(lotNum)}</div>
						   <div style="${validStyle};font-size:0.75rem">${validFmt}</div>`
						: '<span style="color:#aaa;font-size:0.75rem">sem lote</span>'
					}
				</td>
				<td style="padding:5px 8px;vertical-align:middle">
					<input
						type="text"
						class="conf-lot"
						data-number="${escapeHtml(item.number)}"
						data-expected-lot="${hasLot ? escapeHtml(lotNum || '') : ''}"
						data-has-lot="${hasLot ? '1' : '0'}"
						value="${prefLot}"
						${!hasLot ? 'disabled placeholder="—"' : 'placeholder="Digite o lote"'}
						style="width:7rem;padding:2px 4px${!hasLot ? ';color:#aaa' : ''}"
						autocomplete="off"
					>
				</td>
				<td style="padding:5px 8px;vertical-align:middle">
					<input
						type="text"
						class="conf-expiry"
						data-number="${escapeHtml(item.number)}"
						data-expected-expiry="${escapeHtml(validISO || '')}"
						data-has-lot="${hasLot ? '1' : '0'}"
						value="${prefExp}"
						${!hasLot ? 'disabled placeholder="—"' : 'placeholder="DD/MM/AAAA" maxlength="10"'}
						style="width:7.5rem;padding:2px 4px${!hasLot ? ';color:#aaa' : ''}"
						autocomplete="off"
					>
				</td>
				<td style="padding:5px 8px;text-align:right;white-space:nowrap;font-weight:500;vertical-align:middle">${escapeHtml(item.quantity_required)}</td>
			<td style="padding:5px 8px;vertical-align:middle">
				<input
					type="number"
					class="conf-qty"
					data-number="${escapeHtml(item.number)}"
					data-required="${escapeHtml(item.quantity_required)}"
					value="${prefQty}"
					min="0"
					max="${escapeHtml(item.quantity_required)}"
					step="1"
					${enableBoxCtrl && requiresBox ? 'readonly title="Preenchido automaticamente pelas caixas lacradas"' : ''}
					style="width:5.5rem;text-align:right;padding:2px 4px${enableBoxCtrl && requiresBox ? ';background:#f0f7ff;color:#333' : ''}"
					autocomplete="off"
				>
			</td>
			<td class="conf-diff" data-number="${escapeHtml(item.number)}" style="padding:5px 8px;text-align:right;white-space:nowrap;color:${enableBoxCtrl ? bDiffColor : '#888'};vertical-align:middle">${enableBoxCtrl ? bDiffStr : '—'}</td>
			<td style="padding:5px 8px;text-align:center;vertical-align:middle;white-space:nowrap">
				${unpickCell}
			</td>
			</tr>
		<tr class="conf-div-row" data-number="${escapeHtml(item.number)}" style="${initDivShow || (enableBoxCtrl && bDiff < 0) ? '' : 'display:none'}">
			<td colspan="9" style="padding:4px 8px 8px 2rem;background:#fff8e6;border-bottom:1px solid #f0c04d">
					<div style="display:flex;align-items:flex-start;gap:0.5rem;font-size:0.8rem;color:#92600a;margin-bottom:4px">
						<span>⚠</span>
						<span>Quantidade divergente — informe o motivo:</span>
					</div>
					<div style="display:flex;gap:0.5rem;flex-wrap:wrap;align-items:flex-start">
						<select
							class="conf-reason-select"
							data-number="${escapeHtml(item.number)}"
							style="padding:3px 6px;font-size:0.82rem;min-width:200px"
						>
							<option value="">— selecione o motivo —</option>
							${reasonOptions}
						</select>
						<textarea
							class="conf-reason-note"
							data-number="${escapeHtml(item.number)}"
							placeholder="Observação obrigatória para Outros"
							style="display:none;padding:3px 6px;font-size:0.8rem;resize:vertical;min-height:2rem;min-width:240px;flex:1"
						></textarea>
					</div>
				</td>
			</tr>
		`;
	}).join('');

	const checkpointVolumeType = collectorCheckpoint?.volume_type || null;
	const vtOptions = VOLUME_TYPES.map(o =>
	{
		// Caixas ativo → Caixa; senão checkpoint do coletor; senão valor da saída
		const effectiveVt = enableBoxCtrl ? 'C' : (checkpointVolumeType || output.volume_type);
		const sel = effectiveVt === o.value ? ' selected' : '';
		return `<option value="${o.value}"${sel}>${o.label}</option>`;
	}).join('');

	const statusStr = `${output.status} — ${STATUS_LABEL[output.status] || output.status}`;

	// Painel de caixas de picking (quando controle ativo)
	const _BOX_ST_LBL = { 10: 'Aberta', 20: 'Lacrada', 30: 'Cancelada' };
	const _BOX_ST_STYLE = {
		10: 'background:#fff7e6;color:#b45309;border:1px solid #f0c04d',
		20: 'background:#e6f9ee;color:#1a6e38;border:1px solid #6bcf8b',
		30: 'background:#f5f5f5;color:#888;border:1px solid #ccc',
	};

	function buildBoxPanelInnerHtml() {
		const expVolumes = expectedConferenceVolumes();
		const tertiaryCount = packagingActive ? countTertiaryVolumes() : 0;
		const warningBar = boxOpenCount > 0
			? `<div class="conf-box-warning" style="background:#fff3cd;border:1px solid #ffc107;border-radius:3px;padding:5px 10px;font-size:0.8rem;color:#856404;margin-bottom:0.5rem">
					⚠ <strong>${boxOpenCount} caixa(s) ainda em aberto</strong> — lacre todas antes de concluir, ou use Ver Caixas para ajustar.
				</div>`
			: (needsSealedBoxesForConference() && boxClosedCount === 0
				? `<div class="conf-box-warning" style="background:#f8d7da;border:1px solid #f5c2c7;border-radius:3px;padding:5px 10px;font-size:0.8rem;color:#842029;margin-bottom:0.5rem">
					✕ <strong>Nenhuma caixa lacrada</strong> — não será possível concluir a conferência.
				</div>`
				: '');

		const boxRowsHtml = (pickingBoxes || []).map(box => {
			const boxStatus = Number(box.status);
			const bItems    = Array.isArray(box.items) ? box.items : [];
			const bQtyTotal = bItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
			const sealCell = boxStatus === 10
				? (bItems.length > 0
					? `<button type="button" class="conf-box-seal" data-box-id="${escapeHtml(box.id)}" data-box-code="${escapeHtml(box.code || '')}" data-items="${bItems.length}" style="font-size:0.72rem;padding:1px 5px;cursor:pointer">Lacrar Caixa</button>`
					: `<span style="font-size:0.72rem;color:#888" title="Adicione itens antes de lacrar">sem itens</span>`)
				: '—';
			return `<tr style="border-bottom:1px solid #eee">
				<td style="padding:3px 6px;font-size:0.8rem;white-space:nowrap">${escapeHtml(box.code || '—')}</td>
				<td style="padding:3px 6px;font-size:0.8rem;white-space:nowrap;color:#555">${escapeHtml(box.seal_code || '—')}</td>
				<td style="padding:3px 6px;text-align:center">
					<span style="font-size:0.72rem;padding:1px 6px;border-radius:10px;${_BOX_ST_STYLE[boxStatus] || ''}">${escapeHtml(_BOX_ST_LBL[boxStatus] || box.status)}</span>
				</td>
				<td style="padding:3px 6px;text-align:right;font-size:0.8rem">${bItems.length}</td>
				<td style="padding:3px 6px;text-align:right;font-size:0.8rem">${bQtyTotal}</td>
				<td style="padding:3px 6px;text-align:center">${sealCell}</td>
			</tr>`;
		}).join('');

		const statsHtml = `
			<div class="conf-box-stats" style="display:flex;gap:1.2rem;flex-wrap:wrap;font-size:0.8rem;margin-bottom:0.5rem">
				<span>Abertas: <strong style="color:${boxOpenCount > 0 ? '#b45309' : '#888'}">${boxOpenCount}</strong></span>
				<span>Lacradas: <strong style="color:${boxClosedCount > 0 ? '#1a6e38' : '#888'}">${boxClosedCount}</strong></span>
				${tertiaryCount > 0 ? `<span>Terciários: <strong style="color:#6c3483">${tertiaryCount}</strong></span>` : ''}
				${expVolumes > 0 ? `<span>Volumes totais: <strong>${expVolumes}</strong></span>` : ''}
				<span>Canceladas: <strong style="color:#888">${boxCanceledCount}</strong></span>
				<span>Itens nas lacradas: <strong>${boxClosedTotalItems}</strong></span>
				<span>Unidades nas lacradas: <strong>${boxClosedTotalQty}</strong></span>
			</div>`;

		const tableHtml = (pickingBoxes || []).length > 0
			? `<div style="overflow-x:auto;max-height:120px;overflow-y:auto">
				<table style="width:100%;border-collapse:collapse;font-size:0.82rem">
					<thead>
						<tr style="background:#e8f0fb">
							<th style="padding:3px 6px;text-align:left;font-weight:500">Caixa</th>
							<th style="padding:3px 6px;text-align:left;font-weight:500">Lacre</th>
							<th style="padding:3px 6px;text-align:center;font-weight:500">Status</th>
							<th style="padding:3px 6px;text-align:right;font-weight:500">Itens</th>
							<th style="padding:3px 6px;text-align:right;font-weight:500">Qtd</th>
							<th style="padding:3px 6px;text-align:center;font-weight:500">Ação</th>
						</tr>
					</thead>
					<tbody>${boxRowsHtml}</tbody>
				</table>
			</div>`
			: '<div style="font-size:0.8rem;color:#888">Nenhuma caixa encontrada para esta saída.</div>';

		return `${statsHtml}${warningBar}${tableHtml}`;
	}

	let boxCtrlBanner = '';
	if (enableBoxCtrl) {
		boxCtrlBanner = `
			<div id="conf-box-panel" style="background:#f8fbff;border:1px solid #b3d1f5;border-radius:4px;padding:8px 10px;margin-bottom:0.75rem">
				<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:0.5rem;flex-wrap:wrap;margin-bottom:0.35rem">
					<div style="font-weight:600;font-size:0.85rem;color:#1a4a8a">Caixas de Picking</div>
					<div style="display:flex;gap:0.35rem;flex-wrap:wrap">
						<button type="button" class="button conf-btn-view-boxes" style="font-size:0.78rem;padding:2px 8px">Ver Caixas</button>
						<button type="button" class="button conf-btn-gestao-boxes" style="font-size:0.78rem;padding:2px 8px">Abrir Gestão de Caixas</button>
					</div>
				</div>
				<p style="margin:0 0 0.5rem;color:#666;font-size:0.78rem">${boxEscHtml(BOX_CTRL_HINT)}</p>
				<div id="conf-box-panel-inner">${buildBoxPanelInnerHtml()}</div>
			</div>`;
	}

	const checkpointBanner = collectorCheckpoint ? `
		<div style="background:#e8f4fd;border:1px solid #90caf9;border-radius:4px;padding:8px 10px;font-size:0.82rem;margin-bottom:0.75rem;color:#0d47a1">
			<strong>Conferência parcial do ColetorPWA</strong><br>
			Esta saída possui conferência parcial enviada pelo ColetorPWA. Os itens já conferidos foram preservados
			e existe divergência pendente de tratativa.
			${collectorCheckpoint.device_id ? `<br><span style="font-size:0.75rem;color:#555">Dispositivo: ${escapeHtml(collectorCheckpoint.device_id)}</span>` : ''}
			${collectorCheckpoint.operator_id ? `<span style="font-size:0.75rem;color:#555"> · Operador: ${escapeHtml(collectorCheckpoint.operator_id)}</span>` : ''}
		</div>
	` : '';

	const paramBanner = (!allowPartial || requireAuth) ? `
		<div style="background:#fff7e6;border:1px solid #f0c04d;border-radius:4px;padding:6px 10px;font-size:0.8rem;margin-bottom:0.75rem;display:flex;gap:0.5rem;align-items:flex-start">
			<span style="font-size:1rem">⚠</span>
			<span>
				${!allowPartial ? '<strong>Conferência parcial não permitida</strong> para esta empresa — todas as linhas devem ser conferidas integralmente.<br>' : ''}
				${requireAuth   ? '<strong>Autorização exigida:</strong> conferências com divergência de quantidade precisam de um usuário com permissão de autorização.' : ''}
			</span>
		</div>
	` : '';

	const html = `
		<style>
			.conf-modal { min-width: 740px; max-width: 98vw; max-height: 92vh; overflow-y: auto; }
			.conf-modal table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
			.conf-modal thead th {
				background: #f0f0f0; padding: 6px 8px; text-align: left;
				position: sticky; top: 0; border-bottom: 1px solid #ddd; white-space: nowrap;
			}
			.conf-modal tbody tr:nth-child(even) td { background: #f9f9f9; }
			.conf-modal tbody tr:hover td { background: #eef4ff; }
			.conf-modal .conf-footer {
				display: flex; gap: 1rem; flex-wrap: wrap; align-items: flex-end; margin-top: 1rem;
			}
			.conf-modal .conf-footer label { display: flex; flex-direction: column; gap: 4px; font-size: 0.875rem; }
			.conf-modal .conf-footer input,
			.conf-modal .conf-footer select { padding: 3px 6px; }
			.conf-modal .conf-hint { font-size: 0.75rem; color: #888; margin-top: 6px; }
		</style>
		<div class="msgbox conf-modal">
			<div class="mb-title">Conferência de Saída</div>
			<div class="mb-text">
				<div style="display:flex;gap:2rem;flex-wrap:wrap;margin-bottom:0.75rem;font-size:0.875rem">
					<span>Saída: <strong>${escapeHtml(output.id)}</strong></span>
					<span>Pedido: <strong>${escapeHtml(output.order_id)}</strong></span>
					<span>Status: <strong>${escapeHtml(statusStr)}</strong></span>
				</div>
				${boxCtrlBanner}
				${checkpointBanner}
				${paramBanner}
				<div style="overflow-x:auto;max-height:42vh;overflow-y:auto">
					<table>
						<thead>
							<tr>
								<th style="text-align:left;width:2rem">#</th>
								<th style="text-align:left">SKU / Produto</th>
								<th style="text-align:left">Lote sep. / Validade sep.</th>
								<th style="text-align:left">Lote conferido<sup>*</sup></th>
								<th style="text-align:left">Validade conferida<sup>*</sup></th>
								<th style="text-align:right">Req.</th>
								<th style="text-align:right">Conf.</th>
								<th style="text-align:right">Dif.</th>
								<th style="text-align:center;white-space:nowrap">Unpicking</th>
							</tr>
						</thead>
						<tbody id="conf-items-body">
							${itemRows || '<tr><td colspan="9" style="padding:8px;color:#888">Nenhum item encontrado.</td></tr>'}
						</tbody>
					</table>
				</div>
				<p class="conf-hint">Validade: DD/MM/AAAA (ex: 30/12/2099) — as barras são inseridas automaticamente · Lote: sem diferenciar maiúsculas/minúsculas · Quando conferido &lt; requerido aparece o campo de motivo abaixo da linha.</p>
				<div class="conf-footer">
					<label>Tipo Volume<sup>*</sup>
						<select id="conf-volume-type" required>
							<option value="">(selecione)</option>
							${vtOptions}
						</select>
					</label>
				<label>Volumes<sup>*</sup>
					<input id="conf-volumes" type="number" min="1" step="1"
						value="${collectorCheckpoint?.volumes != null ? escapeHtml(collectorCheckpoint.volumes) : (enableBoxCtrl ? expectedConferenceVolumes() || boxClosedCount : (output.volumes != null ? escapeHtml(output.volumes) : ''))}"
						style="width:6rem;text-align:right${enableBoxCtrl ? ';background:#f0f7ff;color:#333' : ''}"
						${enableBoxCtrl ? `readonly title="${packagingActive && countTertiaryVolumes() > 0 ? `Caixas lacradas (${boxClosedCount}) + volumes terciários (${countTertiaryVolumes()})` : 'Preenchido automaticamente — igual ao número de caixas lacradas'}"` : ''}
						required>
				</label>
					<label>Peso total (kg)<sup>*</sup>
						<input id="conf-weight" type="number" min="0" step="0.001"
							value="${collectorCheckpoint?.weight != null ? escapeHtml(collectorCheckpoint.weight) : (output.weight != null ? escapeHtml(output.weight) : '')}"
							style="width:8rem;text-align:right"
							required>
					</label>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Conferir Saída</button>
		</div>
	`;

	const dlg = dialog.create(html, async (key) =>
	{
		if (key === 'Escape' || key === 'N')
		{
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		const node = dlg.getNode();

		// Limpar marcações de erro anteriores
		node.querySelectorAll('.conf-lot,.conf-expiry,.conf-qty,.conf-reason-select,.conf-reason-note').forEach(el => markError(el, false));

		const itemsPayload = [];
		let valid = true;

		// Apenas linhas principais (excluir sub-linhas de motivo)
		const rows = Array.from(node.querySelectorAll('tr[data-number]:not(.conf-div-row)'));

		for (const row of rows)
		{
			const number      = parseInt(row.dataset.number, 10);
			const lotInput    = row.querySelector('.conf-lot');
			const expiryInput = row.querySelector('.conf-expiry');
			const qtyInput    = row.querySelector('.conf-qty');

			const hasLot  = lotInput  && lotInput.dataset.hasLot === '1';
			const required = parseInt(qtyInput.dataset.required, 10);

			// ── Quantidade ─────────────────────────────────────────────────
			const rawQty = qtyInput.value.trim();
			if (rawQty === '' || isNaN(Number(rawQty)))
			{
				markError(qtyInput, true);
				notify(`Informe a quantidade conferida para a linha ${number}.`, 'Conferência de Saída');
				qtyInput.focus();
				valid = false;
				break;
			}
			const qty = parseInt(rawQty, 10);
			if (qty < 0)
			{
				markError(qtyInput, true);
				notify(`Quantidade conferida não pode ser negativa (linha ${number}).`, 'Conferência de Saída');
				qtyInput.focus();
				valid = false;
				break;
			}
			if (qty > required)
			{
				markError(qtyInput, true);
				notify(
					`Quantidade conferida (${qty}) excede a requerida (${required}) na linha ${number}.`,
					'Conferência de Saída'
				);
				qtyInput.focus();
				valid = false;
				break;
			}

			const itemEntry = { number, quantity_supplied: qty };

			// ── Lote e Validade (só para linhas com lote separado) ─────────
			if (hasLot)
			{
				const expectedLot    = lotInput.dataset.expectedLot    || '';
				const expectedExpiry = expiryInput.dataset.expectedExpiry || '';

				// Lote conferido — obrigatório
				const rawLot = lotInput.value.trim();
				if (!rawLot)
				{
					markError(lotInput, true);
					notify(`Informe o lote conferido para a linha ${number}.`, 'Conferência de Saída');
					lotInput.focus();
					valid = false;
					break;
				}
				if (normalizeLot(rawLot) !== normalizeLot(expectedLot))
				{
					markError(lotInput, true);
					notify(
						`Lote conferido não confere com o lote separado na linha ${number}.\n`
						+ `Esperado: "${expectedLot}" — Digitado: "${rawLot}"`,
						'Conferência de Saída'
					);
					lotInput.focus();
					valid = false;
					break;
				}

				// Validade conferida — obrigatória
				const rawExpiry = expiryInput.value.trim();
				if (!rawExpiry)
				{
					markError(expiryInput, true);
					notify(`Informe a validade conferida para a linha ${number}.`, 'Conferência de Saída');
					expiryInput.focus();
					valid = false;
					break;
				}
				const parsedExpiry = parseInputDate(rawExpiry);
				if (!parsedExpiry)
				{
					markError(expiryInput, true);
					notify(
						`Formato de validade inválido na linha ${number}. Use DD/MM/AAAA (ex: 30/12/2099).`,
						'Conferência de Saída'
					);
					expiryInput.focus();
					valid = false;
					break;
				}
				if (parsedExpiry !== expectedExpiry)
				{
					markError(expiryInput, true);
					notify(
						`Validade conferida não confere com a validade do lote separado na linha ${number}.\n`
						+ `Esperada: "${fmtDate(expectedExpiry)}" — Digitada: "${fmtDate(parsedExpiry)}"`,
						'Conferência de Saída'
					);
					expiryInput.focus();
					valid = false;
					break;
				}

				itemEntry.lot_confirmed        = rawLot;
				itemEntry.expiration_confirmed = parsedExpiry;
			}

			// ── Divergência de quantidade ───────────────────────────────────
			if (qty < required)
			{
				// Tenant não permite parcial → bloquear no frontend (backend também bloqueia)
				if (!allowPartial)
				{
					markError(qtyInput, true);
					notify(
						`Esta empresa não permite conferência parcial.\n`
						+ `Linha ${number}: requerido ${required}, conferido ${qty}.\n`
						+ `Confira todas as linhas integralmente.`,
						'Conferência de Saída'
					);
					qtyInput.focus();
					valid = false;
					break;
				}

				// Buscar campos na sub-linha (conf-div-row) correspondente
				const divRow     = node.querySelector(`.conf-div-row[data-number="${number}"]`);
				const reasonSelect = divRow?.querySelector('.conf-reason-select');
				const reasonNote   = divRow?.querySelector('.conf-reason-note');
				const reasonCode   = reasonSelect ? reasonSelect.value.trim() : '';
				const reasonNoteVal = reasonNote ? reasonNote.value.trim() : '';

				if (!reasonCode)
				{
					markError(reasonSelect, true);
					notify(
						`Informe o motivo da divergência para a linha ${number} `
						+ `(conferido ${qty} de ${required}).`,
						'Conferência de Saída'
					);
					reasonSelect?.focus();
					valid = false;
					break;
				}

				if (reasonCode === 'OUTROS' && !reasonNoteVal)
				{
					markError(reasonNote, true);
					notify(
						`A observação é obrigatória quando o motivo for "Outros" (linha ${number}).`,
						'Conferência de Saída'
					);
					reasonNote?.focus();
					valid = false;
					break;
				}

				itemEntry.reason_code = reasonCode;
				if (reasonNoteVal) itemEntry.reason_note = reasonNoteVal;
			}

			itemsPayload.push(itemEntry);
		}

		if (!valid) return;

		// ── Cabeçalho ──────────────────────────────────────────────────────
		const volumeType = node.querySelector('#conf-volume-type').value;
		const volumesRaw = node.querySelector('#conf-volumes').value.trim();
		const weightRaw  = node.querySelector('#conf-weight').value.trim();

		if (!volumeType)
		{
			notify('Selecione o tipo de volume.', 'Conferência de Saída');
			node.querySelector('#conf-volume-type').focus();
			return;
		}

		const volumes = parseInt(volumesRaw, 10);
		if (!volumesRaw || isNaN(volumes) || volumes < 1)
		{
			notify('Informe o número de volumes (mínimo 1).', 'Conferência de Saída');
			node.querySelector('#conf-volumes').focus();
			return;
		}

		const weight = parseFloat(weightRaw);
		if (!weightRaw || isNaN(weight) || weight < 0)
		{
			notify('Informe o peso total dos volumes.', 'Conferência de Saída');
			node.querySelector('#conf-weight').focus();
			return;
		}

	const payload = {
		items: itemsPayload,
		volume_type: volumeType,
		volumes,
		weight,
	};

	// ── Validações de caixas de picking (guarda defensiva antes do backend) ──
	if (enableBoxCtrl) {
		if (!pickingBoxes) {
			notify(
				'Controle de caixas ativo: não foi possível carregar os itens das caixas.',
				'Conferência de Saída'
			);
			return;
		}
		if (needsSealedBoxesForConference() && boxClosedCount === 0) {
			notify(
				'Controle de caixas ativo: não há caixas lacradas para esta saída.',
				'Conferência de Saída'
			);
			return;
		}
		if (boxOpenCount > 0) {
			notify(
				`Controle de caixas ativo: lacre todas as caixas antes de concluir a conferência (${boxOpenCount} caixa(s) em aberto).`,
				'Conferência de Saída'
			);
			return;
		}
		const closedWithoutItems = pickingBoxes.filter(
			b => Number(b.status) === 20 && (!Array.isArray(b.items) || b.items.length === 0)
		);
		if (closedWithoutItems.length > 0) {
			notify(
				`Controle de caixas ativo: ${closedWithoutItems.length} caixa(s) lacrada(s) sem itens registrados.`,
				'Conferência de Saída'
			);
			return;
		}
		const expVolumes = expectedConferenceVolumes();
		if (volumes !== expVolumes) {
			const tertiaryCount = packagingActive ? countTertiaryVolumes() : 0;
			notify(
				tertiaryCount > 0
					? `Controle de caixas ativo: volumes (${volumes}) deve ser igual a caixas lacradas (${boxClosedCount}) + terciários (${tertiaryCount}) = ${expVolumes}.`
					: `Controle de caixas ativo: volumes (${volumes}) deve ser igual ao número de caixas lacradas (${boxClosedCount}).`,
				'Conferência de Saída'
			);
			return;
		}
		for (const entry of itemsPayload) {
			const origItem = items.find(i => i.number === entry.number);
			if (origItem && !itemRequiresPickingBox(origItem)) continue;
			const boxQty = boxSumByLine[entry.number] || 0;
			if (boxQty !== entry.quantity_supplied) {
				notify(
					`Controle de caixas ativo: a quantidade nas caixas diverge da quantidade conferida (linha ${entry.number}: caixas=${boxQty}, conferido=${entry.quantity_supplied}).`,
					'Conferência de Saída'
				);
				return;
			}
		}
	}

	const result = await api.call(api.conference_output, companyId, outputId, payload);
	if (result)
	{
		dlg.destroy();
		if (onSuccess) onSuccess();

		// Verificar se houve falta em alguma linha — oferecer Pedido Falta.
		// itemsPayload só tem {number, quantity_supplied, ...}; a quantity_required vem de items[].
		const enrichedItems = items.map(orig => {
			const supplied = itemsPayload.find(i => i.number === orig.number);
			return { ...orig, quantity_supplied: supplied?.quantity_supplied ?? orig.quantity_required };
		});
		const shortageDetected = enrichedItems.some(i => {
			const req = parseInt(i.quantity_required ?? 0, 10);
			const sup = parseInt(i.quantity_supplied  ?? req, 10);
			return (req - sup) > 0;
		});

		if (shortageDetected && output.order_id)
		{
			openShortageModal(
				companyId,
				parseInt(outputId, 10),
				output.order_id,
				enrichedItems
			);
		}
		else
		{
			notify(`Saída ${outputId} conferida com sucesso.`, 'Conferência de Saída');
		}
	}

	}, /Escape|N|Y/);

	// ── Listeners em tempo real ────────────────────────────────────────────
	const node = dlg.getNode();

	// Diferença em tempo real + exibição de campos de motivo/divergência
	// Quando box ctrl ativo, os inputs são readOnly e não precisam de listener
	node.querySelectorAll('.conf-qty:not([readonly])').forEach(input =>
	{
		input.addEventListener('input', () =>
		{
			markError(input, false);
			const number   = input.dataset.number;
			const required = parseInt(input.dataset.required, 10);
			const qty      = parseInt(input.value, 10);
			const diffCell = node.querySelector(`.conf-diff[data-number="${number}"]`);
			const row      = input.closest('tr');

			if (!diffCell) return;

			if (isNaN(qty))
			{
				diffCell.textContent = '—';
				diffCell.style.color = '#888';
				if (row) toggleDivergenceFields(row, false, allowPartial);
			}
			else
			{
				const diff = qty - required;
				diffCell.textContent = diff === 0 ? '✓' : String(diff);
				diffCell.style.color  = diff === 0 ? '#2a7' : (diff < 0 ? '#c80' : '#c00');

				// Mostrar campos de motivo somente quando há divergência
				if (row) toggleDivergenceFields(row, diff < 0, allowPartial);
			}
		});
	});

	// Exibir/ocultar textarea de observação conforme motivo selecionado
	node.querySelectorAll('.conf-reason-select').forEach(sel =>
	{
		sel.addEventListener('change', () =>
		{
			markError(sel, false);
			const divRow = sel.closest('.conf-div-row');
			const note   = divRow?.querySelector('.conf-reason-note');
			if (note) {
				note.style.display = sel.value === 'OUTROS' ? '' : 'none';
				markError(note, false);
			}
		});
	});

	// Máscara DD/MM/AAAA nos campos de validade
	node.querySelectorAll('.conf-expiry:not([disabled])').forEach(input =>
	{
		input.addEventListener('input', () => {
			applyDateMask(input);
			markError(input, false);
		});
		input.addEventListener('keydown', (e) => {
			// Permite: backspace, delete, tab, setas, home, end
			const allowed = ['Backspace','Delete','Tab','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'];
			if (allowed.includes(e.key)) return;
			// Bloqueia qualquer tecla que não seja dígito
			if (!/^\d$/.test(e.key)) e.preventDefault();
		});
	});

	// Limpar marcação de erro ao digitar nos campos de lote/obs
	node.querySelectorAll('.conf-lot,.conf-reason-note').forEach(input =>
	{
		input.addEventListener('input', () => markError(input, false));
	});

	// ── Botões de unpicking por linha ──────────────────────────────────────
	node.querySelectorAll('.conf-unpick-btn').forEach(btn =>
	{
		btn.addEventListener('click', () => {
			const num  = parseInt(btn.dataset.number, 10);
			const item = items.find(i => i.number === num);
			if (!item) return;

			openUnpickRequestModal(
				companyId,
				outputId,
				item,
				output.status,
				(newRequestId) => {
					// Atualizar a célula na linha para exibir badge REQUESTED
					const cell = btn.closest('td');
					if (cell) {
						cell.innerHTML = `<div class="text-center" style="font-size:0.8rem">
							${unpickStatusBadge('REQUESTED')}
						</div>`;
					}
					// Registrar no mapa para evitar nova solicitação na sessão
					unpickByItem[num] = { status: 'REQUESTED', id: newRequestId };
				}
			);
		});
	});

	// ── Caixas de picking: ações auxiliares e atualização do painel ─────────
	if (enableBoxCtrl) {
		async function refreshConferenceBoxes() {
			try {
				const boxData = await api.call(api.list_output_picking_boxes, companyId, outputId);
				pickingBoxes = boxData?.boxes || [];
			} catch (_) {
				notify('Não foi possível atualizar as caixas de picking.', 'Conferência de Saída');
				return;
			}
			recomputeBoxTotals();

			const inner = node.querySelector('#conf-box-panel-inner');
			if (inner) inner.innerHTML = buildBoxPanelInnerHtml();

			const volInput = node.querySelector('#conf-volumes');
			if (volInput) volInput.value = expectedConferenceVolumes() || boxClosedCount;

			for (const row of node.querySelectorAll('tr[data-number]:not(.conf-div-row)')) {
				const num = parseInt(row.dataset.number, 10);
				const origItem = items.find(i => i.number === num);
				const qtyInput = row.querySelector('.conf-qty');
				const diffCell = row.querySelector('.conf-diff');
				if (!qtyInput || !diffCell) continue;
				const bQtyReq = parseInt(qtyInput.dataset.required, 10) || 0;
				const requiresBox = origItem && itemRequiresPickingBox(origItem);
				const bQty = requiresBox
					? (boxSumByLine[num] || 0)
					: (parseInt(origItem?.quantity_supplied ?? origItem?.quantity_required ?? 0, 10) || 0);
				if (requiresBox) qtyInput.value = bQty;
				const bDiff = bQty - bQtyReq;
				diffCell.textContent = bDiff === 0 ? '✓' : String(bDiff);
				diffCell.style.color = bDiff === 0 ? '#2a7' : (bDiff < 0 ? '#c80' : '#c00');
				const divRow = node.querySelector(`.conf-div-row[data-number="${num}"]`);
				if (divRow) divRow.style.display = bDiff < 0 ? '' : 'none';
			}
		}

		const boxPanel = node.querySelector('#conf-box-panel');
		if (boxPanel && !boxPanel.dataset.bound) {
			boxPanel.dataset.bound = '1';
			boxPanel.addEventListener('click', (ev) => {
				if (ev.target.closest('.conf-btn-view-boxes')) {
					ev.preventDefault();
					openPickingBoxesForOutputModal(companyId, outputId, refreshConferenceBoxes);
					return;
				}
				if (ev.target.closest('.conf-btn-gestao-boxes')) {
					ev.preventDefault();
					location.hash = '#output-picking-boxes';
					return;
				}
				const sealBtn = ev.target.closest('.conf-box-seal');
				if (sealBtn) {
					ev.preventDefault();
					const boxId = sealBtn.getAttribute('data-box-id');
					const boxCode = sealBtn.getAttribute('data-box-code') || boxId;
					const itemCount = parseInt(sealBtn.getAttribute('data-items') || '0', 10);
					openCloseBoxModal(companyId, outputId, boxId, boxCode, itemCount, refreshConferenceBoxes);
				}
			});
		}
	}
}
